import { useState, useMemo, useRef, useEffect } from 'react'
import { reactMount } from '@/lib/reactMount'
import { AccentBar } from '@/components/AccentBar'
import { Button } from '@/components/Button'
import { Chevron, ExclamationCircleOutline, Dollar, InformationCircleFilled, DeleteCircle } from '@/components/Icons'
import { Tooltip } from 'react-tooltip'
import { calculateRepayment, calculateMaxBorrowable, getMinMaxRates, formatCurrency } from '@/lib/loan'
import { NumericFormat } from 'react-number-format'
import { gtmTrackEvent } from '@/lib/analytics'
import { getHeadingClasses } from '@/lib/headings'

const loanTerms = [6, 12, 18, 24, 30, 36, 42, 48, 54, 60]
const formatTermLabel = (months) => {
  return `${months} months`
}

const repaymentFrequencies = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' }
]

// Generate unique ID for each calculator instance
let calculatorInstanceCounter = 0
const generateUniqueId = () => {
  calculatorInstanceCounter++
  const timestamp = Date.now()
  const random = Math.random().toString(36).slice(2, 11)
  return `calc-${calculatorInstanceCounter}-${timestamp}-${random}`
}
const tooltipStyles = '_tooltip !opacity-100 !bg-default-50 !text-default-900 !rounded-lg !p-2.5 !text-xs !font-semibold !shadow-lg !max-w-[250px] !z-[1000]'
function renderTooltipTemplate (template, constants) {
  if (!template) return ''

  return template.replace(
    /{{\s*([A-Z0-9_]+)\s*}}/g,
    (match, key) => constants?.[key] ?? match
  )
}
export const loanCalculatorBlock = ({ dataset, umbraco, version = 'default' }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const theme = umbraco.Theme || 'dark'
  const backgroundColor = umbraco.BlockBackground ? `#${umbraco.BlockBackground}` : '#ffffff'
  const accentBarColor = umbraco.AccentBarColor
  const accentBarPosition = umbraco.AccentBarPosition
  const isBusinessCalculator = umbraco.Type === 'business'
  const isDealerCalculator = umbraco.Type === 'dealer'
  const isPersonalCalculator = umbraco.Type === 'personal'
  // Defaults to the 'small' tier when the CMS doesn't provide a size.
  const headingClasses = getHeadingClasses(umbraco.TitleHeadingSize, { fallback: 'small' })
  const [activeTab, setActiveTab] = useState('loanFor') // 'loanFor' or 'affordToRepay'
  const debouncedTrackInterestRate = useRef(null)

  // Generate unique ID for this calculator instance
  const uniqueId = useMemo(() => generateUniqueId(), [])
  const uniqueClass = `_loanCalculatorBlock-section-${uniqueId}`

  // Get loan purposes from Umbraco
  const loanPurposes = Array.isArray(umbraco.IWantAloanForOptions)
    ? umbraco.IWantAloanForOptions.map(opt => ({
      value: opt.Option,
      label: opt.Option,
      Lowest: opt.Lowest,
      Highest: opt.Highest,
      Default: opt.Default,
      Minimum: opt.Minimum,
      Maximum: opt.Maximum
    }))
    : []

  // Get initial min/max interest rate from all options
  const { min: initialMinRate, max: initialMaxRate } = getMinMaxRates(
    loanPurposes,
    umbraco.LowestInterestRate,
    umbraco.HighestInterestRate
  )

  // Check if there's only one loan purpose option
  const hasSinglePurpose = loanPurposes.length === 1
  const singlePurpose = hasSinglePurpose ? loanPurposes[0] : null

  // Handle edge case: if no loan purposes, ensure we don't break
  const hasValidPurposes = loanPurposes.length > 0

  // Helper functions to get current min/max loan amounts based on selected purpose
  const getCurrentMinLoanAmount = (selectedPurpose) => {
    if (selectedPurpose && selectedPurpose !== '') {
      const selected = loanPurposes.find(opt => opt.value === selectedPurpose)
      if (selected && selected.Minimum > 0) {
        return selected.Minimum
      }
    }
    return umbraco.MinimumLoanAmount
  }

  const getCurrentMaxLoanAmount = (selectedPurpose) => {
    if (selectedPurpose && selectedPurpose !== '') {
      const selected = loanPurposes.find(opt => opt.value === selectedPurpose)
      if (selected && selected.Maximum > 0) {
        return selected.Maximum
      }
    }
    return umbraco.MaximumLoanAmount
  }

  // Calculate initial rate based on single purpose or default logic
  const getInitialRate = () => {
    if (hasSinglePurpose && singlePurpose) {
      // If there's a single purpose, use its default rate or midpoint
      if (singlePurpose.Default !== undefined && singlePurpose.Default > 0) {
        return parseFloat(singlePurpose.Default).toFixed(2)
      } else {
        return ((singlePurpose.Lowest + singlePurpose.Highest) / 2).toFixed(2)
      }
    }
    // Fallback to original logic
    return (umbraco.DefaultInterestRate ? parseFloat(umbraco.DefaultInterestRate).toFixed(2) : ((initialMinRate + initialMaxRate) / 2).toFixed(2))
  }

  // Calculate initial rate range based on single purpose or all options
  const getInitialRateRange = () => {
    if (hasSinglePurpose && singlePurpose) {
      return { min: singlePurpose.Lowest, max: singlePurpose.Highest }
    }
    return { min: initialMinRate, max: initialMaxRate }
  }

  // State for selected min/max interest rate (based on selected purpose)
  const [rateRange, setRateRange] = useState(getInitialRateRange())

  // Function to calculate canChangeInterestRate based on rateRange
  const calculateCanChangeInterestRate = (currentRateRange) => {
    // If min and max are both 0 or equal to each other, return false (can't change because there's no range)
    if (currentRateRange.min === currentRateRange.max) {
      return false
    }
    // In any other case, return true (can change because there's a valid range)
    return true
  }

  // State for canChangeInterestRate indicator
  const [canChangeInterestRate, setCanChangeInterestRate] = useState(() =>
    calculateCanChangeInterestRate(getInitialRateRange())
  )

  // Update canChangeInterestRate whenever rateRange changes
  useEffect(() => {
    setCanChangeInterestRate(calculateCanChangeInterestRate(rateRange))
  }, [rateRange])

  // Get default loan term with fallback
  const defaultLoanTerm = (umbraco.DefaultLoanTerm != null && umbraco.DefaultLoanTerm !== '') ? umbraco.DefaultLoanTerm : 48

  // State for 'I want a loan for' with some defaults
  const [form, setForm] = useState({
    purpose: hasSinglePurpose && hasValidPurposes ? singlePurpose.value : '',
    amount: '',
    term: defaultLoanTerm,
    rate: getInitialRate(), // store as string with 2 decimal places
    frequency: 'weekly',
    balloonPaymentAmount: 0,
    depositAmount: 0
  })

  // State for 'I can afford to repay' with some defaults
  const [afford, setAfford] = useState({
    purpose: hasSinglePurpose && hasValidPurposes ? singlePurpose.value : '',
    repayment: '',
    frequency: 'weekly',
    term: defaultLoanTerm
  })

  // Fee structure
  const fees = {
    establishmentFee: umbraco.LoanEstablishment?.Value,
    dealerEstablishmentFee: umbraco.DealerEstablishmentFee?.Value,
    ppsrFee: umbraco.PpsrFee?.Value,
    maintenanceFee: umbraco.MaintenanceFee?.Value
  }

  const [affordTouched, setAffordTouched] = useState(false)
  const [errors, setErrors] = useState({})
  const [affordErrors, setAffordErrors] = useState({})
  const [showBreakdown, setShowBreakdown] = useState(false)
  const [breakdownOverflowsViewport, setBreakdownOverflowsViewport] = useState(false)
  const breakdownContentRef = useRef(null)
  const mobileCalculatorBlock = useRef(null)
  const [amountTouched, setAmountTouched] = useState(false)
  const [balloonTouched, setBalloonTouched] = useState(false)
  const [depositTouched, setDepositTouched] = useState(false)

  // Indicator for when personal calculator needs purpose selection
  const isPurposeSelected = form.purpose !== ''
  const shouldDisableCertainFields = !isBusinessCalculator && !isDealerCalculator && isPersonalCalculator && !isPurposeSelected

  // Validation for 'I want a loan for'
  const validate = () => {
    const errors = {}
    const loanAmount = parseFloat(form.amount)
    if (!isNaN(loanAmount)) {
      const currentMinAmount = getCurrentMinLoanAmount(form.purpose)
      const currentMaxAmount = getCurrentMaxLoanAmount(form.purpose)

      if (loanAmount > currentMaxAmount) {
        errors.amount = `Loan amount must not exceed $${currentMaxAmount}`
      } else if (loanAmount < currentMinAmount) {
        errors.amount = `Loan amount must be at least $${currentMinAmount}`
      }
    }

    // Balloon payment validation (only if business calculator)
    if (isBusinessCalculator) {
      const balloon = parseFloat(form.balloonPaymentAmount)
      if (form.balloonPaymentAmount && isNaN(balloon)) {
        errors.balloonPaymentAmount = 'Balloon payment amount must be a number'
      } else if (form.balloonPaymentAmount && balloon < 0) {
        errors.balloonPaymentAmount = 'Balloon payment amount cannot be negative'
      }

      const deposit = parseFloat(form.depositAmount)
      if (form.depositAmount && isNaN(deposit)) {
        errors.depositAmount = 'Deposit amount must be a number'
      } else if (form.depositAmount && deposit < 0) {
        errors.depositAmount = 'Deposit amount cannot be negative'
      }
    }
    setErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Validation for 'I can afford to repay'
  const validateAffordToRepay = () => {
    const errors = {}
    const repay = parseFloat(afford.repayment)

    if (!isNaN(repay)) {
      if (repay <= 0) {
        errors.repayment = 'Repayment amount must be greater than $0'
      }
    }

    setAffordErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Calculation logic
  const calculateRepaymentResults = useMemo(() => {
    if (!validate()) return null

    const loanAmount = parseFloat(shouldDisableCertainFields ? 0 : form.amount)
    const balloonPaymentAmount = isNaN(parseFloat(form.balloonPaymentAmount)) ? 0 : parseFloat(form.balloonPaymentAmount)
    const depositAmount = isNaN(parseFloat(form.depositAmount)) ? 0 : parseFloat(form.depositAmount)
    const rate = parseFloat(form.rate)

    if (isNaN(loanAmount) || isNaN(rate) || loanAmount <= 0) return null

    const maintenanceFeePerPeriod = (umbraco.MaintenanceFee?.Hide ? 0 : (fees.maintenanceFee || 0))
    const totalEstFee = umbraco.LoanEstablishment?.Hide ? 0 : ((fees.establishmentFee || 0) + (umbraco.DealerEstablishmentFee?.Hide ? 0 : (fees.dealerEstablishmentFee || 0)))
    const totalPpsrFee = umbraco.PpsrFee?.Hide ? 0 : (fees.ppsrFee || 0)

    const results = calculateRepayment({
      loanAmount,
      repaymentInMonths: form.term,
      interestRate: rate,
      loanEstablishmentFee: totalEstFee,
      PPSRfee: totalPpsrFee,
      balloonPaymentAmount,
      depositAmount,
      maintenanceFee: maintenanceFeePerPeriod,
      repaymentFrequency: form.frequency,
      paymentTiming: 0
    })
    return results
  }, [form])

  // Calculation for 'I can afford to repay' (reverse amortization)
  const calculateMaxBorrowableResults = useMemo(() => {
    if (!validateAffordToRepay()) return null

    const maintenanceFeePerPeriod = (umbraco.MaintenanceFee?.Hide ? 0 : (fees.maintenanceFee || 0))
    const totalEstFee = umbraco.LoanEstablishment?.Hide ? 0 : ((fees.establishmentFee || 0) + (umbraco.DealerEstablishmentFee?.Hide ? 0 : (fees.dealerEstablishmentFee || 0)))
    const totalPpsrFee = umbraco.PpsrFee?.Hide ? 0 : (fees.ppsrFee || 0)

    const results = calculateMaxBorrowable({
      repaymentInMonths: afford.term,
      interestRate: form.rate,
      loanEstablishmentFee: totalEstFee,
      PPSRfee: totalPpsrFee,
      maintenanceFee: maintenanceFeePerPeriod,
      repaymentFrequency: afford.frequency,
      affordablePerPeriod: afford.repayment,
      paymentTiming: 0
    })

    if (results.errorReason) {
      return {
        results,
        repaymentResultsForBorrowable: null
      }
    } else {
      const repaymentResultsForBorrowable = calculateRepayment({
        loanAmount: results.display.loanAmount,
        repaymentInMonths: afford.term,
        interestRate: form.rate,
        loanEstablishmentFee: totalEstFee,
        PPSRfee: totalPpsrFee,
        depositAmount: 0,
        maintenanceFee: maintenanceFeePerPeriod,
        repaymentFrequency: afford.frequency,
        paymentTiming: 0
      })

      const completeResults = {
        results,
        repaymentResultsForBorrowable
      }
      return completeResults
    }
  }, [afford])

  // Handlers
  const handleChange = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value }
      if (field === 'purpose') {
        // Find the selected option
        const selected = loanPurposes.find(opt => opt.value === value)
        if (selected) {
          setRateRange({ min: selected.Lowest, max: selected.Highest })
          // Use Default if available, otherwise preserve current rate if within bounds or use midpoint
          const currentRate = parseFloat(updated.rate)
          if (selected.Default !== undefined && selected.Default > 0) {
            updated.rate = parseFloat(selected.Default).toFixed(2)
          } else {
            const isWithin = currentRate >= selected.Lowest && currentRate <= selected.Highest
            const midpoint = (selected.Lowest + selected.Highest) / 2
            updated.rate = (isWithin ? currentRate : midpoint).toFixed(2)
          }
          // Update amount if it's outside the new range
          const currentAmount = parseFloat(updated.amount)
          const newMinAmount = getCurrentMinLoanAmount(value)
          const newMaxAmount = getCurrentMaxLoanAmount(value)

          if (isNaN(currentAmount) || currentAmount < newMinAmount) {
            // If amount is below the new minimum, set to minimum
            updated.amount = newMinAmount.toString()
          } else if (currentAmount > newMaxAmount) {
            // If amount is above the new maximum, set to maximum
            updated.amount = newMaxAmount.toString()
          }
        } else {
          // Reset to initial min/max if no purpose selected
          setRateRange({ min: initialMinRate, max: initialMaxRate })
          const currentRate = parseFloat(updated.rate)
          const isWithin = currentRate >= initialMinRate && currentRate <= initialMaxRate
          const midpoint = (initialMinRate + initialMaxRate) / 2
          updated.rate = (isWithin ? currentRate : midpoint).toFixed(2)
          // Update amount if it's outside the default range
          const currentAmount = parseFloat(updated.amount)
          const defaultMinAmount = getCurrentMinLoanAmount('')
          const defaultMaxAmount = getCurrentMaxLoanAmount('')

          if (isNaN(currentAmount) || currentAmount < defaultMinAmount) {
            // If amount is below the default minimum, set to minimum
            updated.amount = defaultMinAmount.toString()
          } else if (currentAmount > defaultMaxAmount) {
            // If amount is above the default maximum, set to maximum
            updated.amount = defaultMaxAmount.toString()
          }
        }
      }
      return updated
    })
  }

  const handleAffordChange = (field, value) => {
    setAfford(f => {
      const updated = { ...f, [field]: value }
      if (field === 'purpose') {
        // Find the selected option
        const selected = loanPurposes.find(opt => opt.value === value)
        if (selected) {
          setRateRange({ min: selected.Lowest, max: selected.Highest })
          // Update the form rate as well to keep it in sync
          setForm(formState => {
            const currentRate = parseFloat(formState.rate)
            if (selected.Default !== undefined && selected.Default > 0) {
              return { ...formState, rate: parseFloat(selected.Default).toFixed(2) }
            } else {
              const isWithin = currentRate >= selected.Lowest && currentRate <= selected.Highest
              const midpoint = (selected.Lowest + selected.Highest) / 2
              return { ...formState, rate: (isWithin ? currentRate : midpoint).toFixed(2) }
            }
          })
        } else {
          // Reset to initial min/max if no purpose selected
          setRateRange({ min: initialMinRate, max: initialMaxRate })
          setForm(formState => {
            const currentRate = parseFloat(formState.rate)
            const isWithin = currentRate >= initialMinRate && currentRate <= initialMaxRate
            const midpoint = (initialMinRate + initialMaxRate) / 2
            return { ...formState, rate: (isWithin ? currentRate : midpoint).toFixed(2) }
          })
        }
      }
      return updated
    })
  }

  const themeCta = umbraco.Theme?.Cta || '' // Theme CTA color
  const themeSecondary = umbraco.Theme?.Secondary || '' // Theme secondary color
  const themeBG = umbraco.Theme?.Background || '' // Theme background color
  const themeApplyNowColor = umbraco.Theme?.ApplyNowColor || '' // Theme apply now button color

  // Only if theme is present, then override body background color with white
  if (themeCta !== '' || themeSecondary !== '' || themeBG !== '') {
    document.body.style.backgroundColor = '#ffffff'
  }

  const tooltipConstants = {
    MINIMUM_LOAN_AMOUNT: formatCurrency(umbraco.MinimumLoanAmount),
    MAXIMUM_LOAN_AMOUNT: formatCurrency(umbraco.MaximumLoanAmount),
    DEFAULT_INTEREST_RATE: `${umbraco.DefaultInterestRate}%`,
    MINIMUM_INTEREST_RATE: `${umbraco.LowestInterestRate}%`,
    MAXIMUM_INTEREST_RATE: `${umbraco.HighestInterestRate}%`,
    DEFAULT_LOAN_TERM: formatTermLabel(umbraco.DefaultLoanTerm),
    ESTABLISHMENT_FEE: formatCurrency(fees.establishmentFee),
    DEALER_ESTABLISHMENT_FEE: formatCurrency(fees.dealerEstablishmentFee),
    PPSR_FEE: formatCurrency(fees.ppsrFee),
    MAINTENANCE_FEE: formatCurrency(fees.maintenanceFee)
  }
  const [showMobileCalculateButton, setShowMobileCalculateButton] = useState(true)

  const checkBreakdownOverflowsViewport = () => {
    if (typeof window === 'undefined') return
    const isMobile = window.innerWidth < 768
    const el = breakdownContentRef.current
    if (!isMobile || !el) {
      setBreakdownOverflowsViewport(false)
      return
    }
    setBreakdownOverflowsViewport(el.offsetHeight > window.innerHeight)
  }

  useEffect(() => {
    if (!showBreakdown) {
      setBreakdownOverflowsViewport(false)
      return
    }
    const runCheck = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(checkBreakdownOverflowsViewport)
      })
    }
    runCheck()
    window.addEventListener('resize', checkBreakdownOverflowsViewport)
    return () => window.removeEventListener('resize', checkBreakdownOverflowsViewport)
  }, [showBreakdown])

  const handleCalculateButton = () => {
    setShowMobileCalculateButton(false)
    // Scroll after React has rendered and mobile overview panel is visible (ref per instance — no duplicate ids)
    setTimeout(() => {
      const el = mobileCalculatorBlock.current
      if (el && typeof window !== 'undefined') {
        const rect = el.getBoundingClientRect()
        // Document Y of the element's bottom edge (viewport rect + current scroll)
        const elementBottomDocY = rect.bottom + window.scrollY
        // Scroll so that bottom of the panel meets the bottom of the viewport
        const targetTop = elementBottomDocY - window.innerHeight
        window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
      }
    }, 150)
  }

  return (
    <div
      data-theme={theme}
      id={umbraco.AnchorTarget || undefined}
      className={`relative _block-section _loanCalculatorBlock-section ${uniqueClass} ${version === 'hero-carousel' ? '!bg-transparent first:-mt-[56px] xl:first:-mt-[146px] z-20 relative mb-8 last:mb-0 last:pb-16' : ''}`}
      style={{ backgroundColor }}
    >
      {/* These styles should only be added if dealer calculator and themes applied */}
      {themeCta !== '' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .${uniqueClass} {
              --calc-cta: ${themeCta};
              --calc-theme-600: ${themeCta};
              --calc-theme-700: ${themeCta};
              --calc-theme-chevron: ${themeCta};
              --calc-theme-btn: ${themeCta};
            }
          `
          }}
        />
      )}
      {themeSecondary !== '' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .${uniqueClass} {
                --calc-secondary: ${themeSecondary};
                --calc-theme-900: ${themeSecondary};
                --calc-theme-950: ${themeSecondary};
                --calc-theme-btn-hover: ${themeSecondary};
              }
            `
          }}
        />
      )}
      {themeBG !== '' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .${uniqueClass} {
                --calc-bg: ${themeBG};
                --calc-theme-50: ${themeBG};
              }
            `
          }}
        />
      )}
      {themeApplyNowColor !== '' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .${uniqueClass} {
                --calc-apply-now: ${themeApplyNowColor};
              }
            `
          }}
        />
      )}
      {themeCta !== '' && themeBG !== '' && themeSecondary !== '' && themeSecondary !== '' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              ._calc-button-primary:before,
              ._calc-button-secondary:before {
                background-image : none !important;
              }
              .${uniqueClass} {
                --calc-theme-800: white;
              }
            `
          }}
        />
      )}
      {/* Top or bottom is not coming from Umbraco it is a design difference for the calculator block */}
      <AccentBar className='h-[360px] z-0' vertical='bottom' position={accentBarPosition} color={accentBarColor} umbraco={umbraco} />
      {(umbraco.Title || umbraco.Summary) && (
        <div className={`${version === 'hero-carousel' ? 'text-default-50' : ''} w-full text-center max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10`}>
          {umbraco.Title && <h2 className={`${headingClasses} font-extrabold mb-2 tracking-tight text-pretty`}>{umbraco.Title}</h2>}
          {umbraco.Summary && <p className={`${version === 'hero-carousel' ? '' : 'text-[--base-text]'} mx-auto max-w-2xl text-base font-semibold`}>{umbraco.Summary}</p>}
        </div>
      )}

      <div className={`w-full max-w-[1072px] mx-auto sm:px-6 lg:px-8 relative ${version === 'hero-carousel' ? 'pt-10 pb-0' : 'py-10'}`}>
        {/* Calculator section used for iframe : maybe this needs to be separated out as its own component? */}
        <div className='relative flex flex-col rounded-lg shadow-lg overflow-hidden max-md:overflow-initial'>
          {/* Breakdown Modal Overlay */}
          <div className={`${showBreakdown ? 'bg-opacity-40 max-md:pointer-events-auto breakdown-modal-overlay max-md:overflow-auto' : 'bg-opacity-0 max-md:pointer-events-none'} transition absolute max-md:fixed max-md:z-[999] inset-0 z-30 flex items-end justify-center ${breakdownOverflowsViewport ? 'max-md:items-start' : 'max-md:items-center'} bg-black rounded-lg max-md:rounded-none duration-600 overflow-hidden pointer-events-none`}>
            <div ref={breakdownContentRef} className={`${showBreakdown ? 'translate-y-0 pointer-events-auto max-md:opacity-100' : 'max-md:translate-y-[calc(100%_+_32px)] max-md:opacity-0 translate-y-[calc(100%_+_80px)] pointer-events-none'} breakdown-content relative w-full transition ease-out duration-600 rounded-lg max-md:rounded-none max-md:h-auto max-md:pb-8 max-md:pt-0 h-[calc(100%-46px)] bg-[--calc-theme-900] max-md:bg-[--calc-theme-950] max-md:bg-gradient-to-r max-md:from-0% max-md:from-[--calc-theme-900] max-md:to-[--calc-theme-950] shadow-2xl p-0 overflow-visible animate-slide-in-bottom pointer-events-auto`}>
              {/* Close breakdown button */}
              <div className='absolute left-1/2 -translate-x-1/2 -top-11 p-2 rounded-t-[8px] bg-[--calc-theme-900] max-md:hidden'>
                <button
                  className='bg-[--calc-theme-700] text-default-50 text-xs font-extrabold rounded px-4 py-1.5 border border-[rgba(255,255,255,0.3)] shadow-md z-10'
                  onClick={() => setShowBreakdown(false)}
                  style={{ minWidth: 120 }}
                >
                  Close breakdown
                </button>
              </div>
              <div className='flex flex-col md:flex-row gap-4 md:gap-8 p-4 max-md:px-8 md:p-8 max-md:pt-0 h-full'>
                {/* Left: Repayment and Fees */}
                <div className='flex-1 flex flex-col min-w-[260px] justify-center max-md:pt-2'>
                  <div className='md:hidden flex justify-between gap-3 items-center sticky mb-5 pt-5 pb-3 bg-[--calc-theme-950] bg-gradient-to-r from-0% from-[--calc-theme-900] to-[--calc-theme-950] before:opacity-30 before:content-[""] before:border-b-2 before:border-default-50 before:absolute before:bottom-0 before:left-0 before:right-0 top-0 z-10'>
                    <p className='text-default-50 max-md:pl-4 font-extrabold text-[18px]'>Cost Breakdown</p>
                    <button className='text-default-50 relative -top-3' onClick={() => setShowBreakdown(false)}>
                      <DeleteCircle className=' pointer-events-none size-9' />
                    </button>
                  </div>
                  <div className='text-[20px] max-md:pl-4 max-md:text-[18px] font-extrabold text-default-50 leading-[28px] mb-2'>Overview</div>
                  {/* Fees */}
                  <div className='flex flex-col gap-3 mt-2'>
                    {!umbraco.FixedAnnualInterestFee?.Hide &&
                      <BreakdownFee
                        label='Fixed annual interest rate'
                        value={form.rate + '% p.a.'}
                        uid='fixed-annual-interest-rate'
                        tooltip={umbraco.FixedAnnualInterestFee?.Tooltip}
                        tooltipConstants={tooltipConstants}
                      />}
                    <div className='flex max-md:flex-col gap-3 items-end'>
                      {!umbraco.LoanEstablishment?.Hide &&
                        <BreakdownFee
                          label={
                            !umbraco.DealerEstablishmentFee?.Hide && fees.dealerEstablishmentFee > 0
                              ? 'UDC loan establishment fee'
                              : 'Loan establishment fee'
                          }
                          value={formatCurrency(fees.establishmentFee)}
                          uid='loan-establishment-fee'
                          tooltip={umbraco.LoanEstablishment?.Tooltip}
                          tooltipConstants={tooltipConstants}
                        />}
                      {!umbraco.DealerEstablishmentFee?.Hide && fees.dealerEstablishmentFee > 0 &&
                        <BreakdownFee
                          label='Dealer loan establishment fee'
                          value={formatCurrency(fees.dealerEstablishmentFee)}
                          uid='dealer-loan-establishment-fee'
                          tooltip={umbraco.DealerEstablishmentFee?.Tooltip}
                          tooltipConstants={tooltipConstants}
                        />}
                    </div>
                    {!umbraco.PpsrFee?.Hide &&
                      <BreakdownFee
                        label='PPSR fee'
                        value={formatCurrency(fees.ppsrFee)}
                        uid='ppsr-fee'
                        tooltip={umbraco.PpsrFee?.Tooltip}
                        tooltipConstants={tooltipConstants}
                      />}
                    {!umbraco.MaintenanceFee?.Hide && activeTab === 'loanFor' &&
                      <BreakdownFee
                        label='Maintenance fee'
                        value={formatCurrency(form.frequency === 'weekly'
                          ? fees.maintenanceFee
                          : form.frequency === 'fortnightly'
                            ? fees.maintenanceFee * 2
                            : fees.maintenanceFee * 4) +
                          (form.frequency === 'weekly'
                            ? '/week'
                            : form.frequency === 'fortnightly'
                              ? '/fortnight'
                              : '/month')}
                        uid='maintenance-fee'
                        tooltip={umbraco.MaintenanceFee?.Tooltip}
                        tooltipConstants={tooltipConstants}
                      />}
                    {!umbraco.MaintenanceFee?.Hide && activeTab === 'affordToRepay' &&
                      <BreakdownFee
                        label='Maintenance fee'
                        value={formatCurrency(afford.frequency === 'weekly'
                          ? fees.maintenanceFee
                          : afford.frequency === 'fortnightly'
                            ? fees.maintenanceFee * 2
                            : fees.maintenanceFee * 4) +
                          (afford.frequency === 'weekly'
                            ? '/week'
                            : afford.frequency === 'fortnightly'
                              ? '/fortnight'
                              : '/month')}
                        uid='maintenance-fee'
                        tooltip={umbraco.MaintenanceFee?.Tooltip}
                        tooltipConstants={tooltipConstants}
                      />}
                  </div>
                </div>
                {/* Right: Overview */}
                <div className='flex-1 flex flex-col gap-4 md:gap-8 max-md:mt-4 bg-[rgba(255,255,255,0.08)] rounded-lg p-4 min-w-[260px]'>
                  {/* Repayment header and frequency selector */}
                  <div className='flex flex-row items-center justify-between gap-5 w-full'>
                    {activeTab === 'loanFor'
                      ? <p className='text-[44px] max-md:text-[22px] font-extrabold text-default-50 tracking-tight leading-[1] pb-2'>{(calculateRepaymentResults && !isNaN(calculateRepaymentResults.periodicRepaymentWithMaintenance) && isFinite(calculateRepaymentResults.periodicRepaymentWithMaintenance)) ? formatCurrency(calculateRepaymentResults.periodicRepaymentWithMaintenance) : '$0.00'}</p>
                      : <p className='text-[44px] max-md:text-[22px] font-extrabold text-default-50 tracking-tight leading-[1] pb-2'>{(calculateMaxBorrowableResults && calculateMaxBorrowableResults.repaymentResultsForBorrowable && !isNaN(calculateMaxBorrowableResults.repaymentResultsForBorrowable.periodicRepaymentWithMaintenance) && isFinite(calculateMaxBorrowableResults.repaymentResultsForBorrowable.periodicRepaymentWithMaintenance)) ? formatCurrency(calculateMaxBorrowableResults.repaymentResultsForBorrowable.periodicRepaymentWithMaintenance) : '$0.00'}</p>}
                    <div className='flex items-center'>
                      {activeTab === 'loanFor'
                        ? <select
                            className='cursor-pointer h-11 text-default-900 font-semibold text-base md:text-[14px] appearance-none pb-px pl-3 pr-6 outline-none bg-default-50 rounded-lg shadow'
                            value={form.frequency}
                            onChange={e => {
                              handleChange('frequency', e.target.value)

                              gtmTrackEvent('setFrequency', { calculatorType: umbraco.Type, tab: activeTab, frequency: e.target.value })
                            }}
                          >
                          {repaymentFrequencies.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label.charAt(0).toUpperCase() + opt.label.slice(1)}</option>
                          ))}
                        </select>
                        : <select
                            className='cursor-pointer h-11 text-default-900 font-semibold text-base md:text-[14px] appearance-none pb-px pl-3 pr-6 outline-none bg-default-50 rounded-lg shadow'
                            value={afford.frequency}
                            onChange={e => {
                              handleAffordChange('frequency', e.target.value)

                              gtmTrackEvent('setFrequency', { calculatorType: umbraco.Type, tab: activeTab, frequency: e.target.value })
                            }}
                          >
                          {repaymentFrequencies.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label.charAt(0).toUpperCase() + opt.label.slice(1)}</option>
                          ))}
                        </select>}
                      <Chevron className='text-[--calc-theme-chevron] pointer-events-none size-5 -ml-[24px] mr-1' />
                    </div>
                  </div>

                  <div className='bg-[rgba(255,255,255,0.08)] rounded-lg p-4 max-md:py-2 flex flex-row items-center justify-between mt-0 md:mt-2'>
                    <span className='text-[14px] font-extrabold text-default-50 flex items-center gap-2 relative'>
                      Total to repay
                      {umbraco.TotalToRepayTooltip && umbraco.TotalToRepayTooltip.trim() && (
                        <>
                          <Tooltip id='total-to-repay-tooltip' className={tooltipStyles} />
                          <span data-tooltip-id='total-to-repay-tooltip' data-tooltip-content={renderTooltipTemplate(umbraco.TotalToRepayTooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5 max-md:top-0.5 max-md:left-0'>
                            <InformationCircleFilled className='w-2.5 h-2.5 max-md:size-5' />
                          </span>
                        </>
                      )}
                    </span>
                    {activeTab === 'loanFor'
                      ? <span className='text-[20px] max-lg:text-sm font-extrabold text-default-50'>{calculateRepaymentResults ? formatCurrency(calculateRepaymentResults.display.totalRepaidWithMaintenance) : '$0.00'}</span>
                      : <span className='text-[20px] max-lg:text-sm font-extrabold text-default-50'>{calculateMaxBorrowableResults && calculateMaxBorrowableResults.repaymentResultsForBorrowable ? formatCurrency(calculateMaxBorrowableResults.repaymentResultsForBorrowable.display.totalRepaidWithMaintenance) : '$0.00'}</span>}
                  </div>
                  <div className='flex flex-col gap-3'>
                    {activeTab === 'loanFor'
                      ? <BreakdownOverview label='Includes interest of' value={calculateRepaymentResults ? formatCurrency(calculateRepaymentResults.display.totalInterest) : '$0.00'} />
                      : <BreakdownOverview label='Includes interest of' value={calculateMaxBorrowableResults && calculateMaxBorrowableResults.repaymentResultsForBorrowable ? formatCurrency(calculateMaxBorrowableResults.repaymentResultsForBorrowable.display.totalInterest) : '$0.00'} />}
                  </div>
                  {umbraco.BreakdownDisclaimer && (
                    <div className='text-[12px] max-md:text-[10px] max-md:font-medium font-bold text-default-300 mt-1 md:mt-2 opacity-90 md:h-[130px] overflow-auto'>
                      {umbraco.BreakdownDisclaimer}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          {!isBusinessCalculator && !isDealerCalculator && (
            <div className='flex flex-row w-full'>
              <button
                className={`flex-1 px-4 sm:px-8 py-4 sm:py-5 font-extrabold text-sm md:text-xl tracking-tight ${activeTab === 'loanFor' ? 'bg-[--calc-theme-50] text-[--calc-theme-600]' : 'bg-[--calc-theme-950] text-[--calc-theme-800]'}`}
                onClick={() => {
                  setActiveTab('loanFor')

                  gtmTrackEvent('setActiveTab', { calculatorType: umbraco.Type, tab: 'loanFor' })
                }}
              >
                <span className={`${showBreakdown ? 'opacity-50' : 'opacity-100'} transition-opacity duration-600`}>I want a loan for</span>
              </button>
              <button
                className={`flex-1 px-4 sm:px-8 py-4 sm:py-5 font-extrabold text-sm md:text-xl tracking-tight ${activeTab === 'affordToRepay' ? 'bg-[--calc-theme-50] text-[--calc-theme-600]' : 'bg-[--calc-theme-950] text-[--calc-theme-800]'}`}
                onClick={() => {
                  setActiveTab('affordToRepay')

                  gtmTrackEvent('setActiveTab', { calculatorType: umbraco.Type, tab: 'affordToRepay' })
                }}
              >
                <span className={`${showBreakdown ? 'opacity-50' : 'opacity-100'} transition-opacity duration-600`}>I can afford to repay</span>
              </button>
            </div>
          )}
          <div className='flex flex-col md:flex-row w-full'>
            {/* Left: Form */}
            <div className='flex-1 flex flex-col bg-[--calc-theme-50] justify-center gap-4 px-8 lg:px-12 py-5 md:min-h-[540px] min-w-[320px] max-md:pt-4'>
              {activeTab === 'loanFor'
                ? (
                  <>
                    {/* Purpose Dropdown */}
                    {!isBusinessCalculator && !isDealerCalculator && (
                      <div className={`${showBreakdown ? 'opacity-0' : 'opacity-100'} transition-opacity duration-600`}>
                        <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I want a loan for</label>
                        <div className='relative'>
                          <select
                            className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 pb-px pr-10 pl-3 font-semibold text-base md:text-[14px] text-default-700 appearance-none shadow-sm'
                            value={form.purpose}
                            onChange={e => {
                              handleChange('purpose', e.target.value)

                              gtmTrackEvent('setPurpose', { calculatorType: umbraco.Type, tab: activeTab, purpose: e.target.value })
                            }}

                          >
                            {!hasSinglePurpose && hasValidPurposes && <option value=''>Select</option>}
                            {loanPurposes.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                        </div>
                      </div>
                    )}
                    {/* Amount Input */}
                    <div className={`${showBreakdown ? 'opacity-0' : 'opacity-100'} transition-opacity duration-600`}>
                      <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I want to borrow</label>
                      <div className='relative'>
                        <span className='absolute left-3 top-1/2 -translate-y-1/2'><Dollar className='size-3' /></span>
                        <input
                          type='number'
                          disabled={shouldDisableCertainFields}
                          className={`${errors.amount && amountTouched ? 'border-[--error] focus:border-[--error]' : 'border-default-200 focus:border-default-400'} bg-default-50 outline-none h-11 w-full rounded-lg border pb-px pl-8 pr-3 font-semibold text-base md:text-[14px] text-default-700 shadow-sm disabled:pointer-events-none disabled:!text-default-400`}
                          placeholder={getCurrentMinLoanAmount(form.purpose)}
                          value={form.amount}
                          onChange={e => {
                            handleChange('amount', e.target.value)
                            if (!amountTouched) setAmountTouched(true)
                          }}
                          onBlur={() => {
                            setAmountTouched(true); validate()

                            gtmTrackEvent('setAmount', { calculatorType: umbraco.Type, tab: activeTab, amount: form.amount })
                          }}
                          min={getCurrentMinLoanAmount(form.purpose)}
                          max={getCurrentMaxLoanAmount(form.purpose)}
                        />
                      </div>
                      {errors.amount && amountTouched && <p className='text-[--error] font-extrabold text-xs mt-2'><ExclamationCircleOutline className='inline-block -translate-y-0.5' /> {errors.amount}</p>}
                    </div>
                    {/* Deposit Amount Input (Business only) */}
                    {isBusinessCalculator && (
                      <div>
                        <label className='text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2 flex items-center gap-2 relative'>
                          I have a deposit of
                          {umbraco.DepositTooltip && umbraco.DepositTooltip.trim() && (
                            <>
                              <Tooltip id='deposit-tooltip' className={tooltipStyles} />
                              <span data-tooltip-id='deposit-tooltip' data-tooltip-content={renderTooltipTemplate(umbraco.DepositTooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5'>
                                <InformationCircleFilled className='w-2.5 h-2.5' />
                              </span>
                            </>
                          )}
                        </label>
                        <div className='relative'>
                          <span className='absolute left-3 top-1/2 -translate-y-1/2'><Dollar className='size-3' /></span>
                          <input
                            type='number'
                            className={`${errors.depositAmount && depositTouched ? 'border-[--error] focus:border-[--error]' : 'border-default-200 focus:border-default-400'} bg-default-50 outline-none h-11 w-full rounded-lg border pb-px pl-8 pr-3 font-semibold text-base md:text-[14px] text-default-700 shadow-sm`}
                            placeholder='0'
                            value={form.depositAmount}
                            onChange={e => {
                              handleChange('depositAmount', e.target.value)
                              if (!depositTouched) setDepositTouched(true)
                            }}
                            onBlur={() => { setDepositTouched(true); validate() }}
                            min={0}
                          />
                        </div>
                        {errors.depositAmount && depositTouched && <p className='text-[--error] font-extrabold text-xs mt-2'><ExclamationCircleOutline className='inline-block -translate-y-0.5' /> {errors.depositAmount}</p>}
                      </div>
                    )}
                    {/* Term Dropdown */}
                    <div>
                      <label className='text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2 flex items-center gap-2 relative'>
                        I'll repay over
                        {umbraco.PaymentScheduleTooltip && umbraco.PaymentScheduleTooltip.trim() && (
                          <>
                            <Tooltip id='repay-over-tooltip' className={tooltipStyles} />
                            <span data-tooltip-id='repay-over-tooltip' data-tooltip-content={renderTooltipTemplate(umbraco.PaymentScheduleTooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5'>
                              <InformationCircleFilled className='w-2.5 h-2.5' />
                            </span>
                          </>
                        )}
                      </label>
                      <div className='relative'>
                        <select
                          className={`${shouldDisableCertainFields ? 'cursor-not-allowed text-default-400 pointer-events-none' : 'cursor-pointer text-default-700'} bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 pb-px pr-10 pl-3 font-semibold text-base md:text-[14px] appearance-none shadow-sm`}
                          value={form.term}
                          onChange={e => {
                            handleChange('term', parseInt(e.target.value))

                            gtmTrackEvent('setTerm', { calculatorType: umbraco.Type, tab: activeTab, term: e.target.value })
                          }}
                        >
                          {loanTerms.map(m => (
                            <option key={m} value={m}>{formatTermLabel(m)}</option>
                          ))}
                        </select>
                        <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                      </div>
                    </div>
                    {/* Interest Rate Slider */}
                    <div>
                      <label className='text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2 flex items-center gap-2 relative'>
                        At an interest rate of
                        {umbraco.InterestRateTooltip && umbraco.InterestRateTooltip.trim() && (
                          <>
                            <Tooltip id='interest-rate-tooltip' className={tooltipStyles} />
                            <span data-tooltip-id='interest-rate-tooltip' data-tooltip-content={renderTooltipTemplate(umbraco.InterestRateTooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5'>
                              <InformationCircleFilled className='w-2.5 h-2.5' />
                            </span>
                          </>
                        )}
                      </label>
                      <div className='relative mb-2'>
                        {/* <span className='absolute left-3 top-1/2 -translate-y-1/2 font-extrabold text-sm -mt-0.5'>%</span> */}
                        {/* Number input for interest rate, synced with range */}
                        <NumericFormat
                          value={shouldDisableCertainFields ? 0 : form.rate}
                          disabled={shouldDisableCertainFields}
                          valueIsNumericString
                          allowNegative={false}
                          decimalScale={2}
                          allowLeadingZeros={false}
                          inputMode='decimal'
                          suffix='%'
                          readOnly={!canChangeInterestRate}
                          tabIndex={canChangeInterestRate ? 0 : -1}
                          isAllowed={({ floatValue, value }) => {
                            // Allow empty and trailing decimal while typing
                            if (value === '' || value?.endsWith?.('.')) return true
                            if (floatValue === undefined) return true
                            // Block values above max 100 while allowing below min (clamped on blur)
                            return floatValue <= 100
                          }}
                          onValueChange={(values) => {
                            if (!canChangeInterestRate) return
                            const val = parseFloat(values?.value ?? '')
                            if (!isNaN(val) && val >= rateRange.min && val <= rateRange.max) {
                              handleChange('rate', values.value)
                            }
                          }}
                          onBlur={e => {
                            if (!canChangeInterestRate) return
                            let val = parseFloat(e.target.value)
                            if (isNaN(val)) val = rateRange.min
                            if (val < rateRange.min) val = rateRange.min
                            if (val > rateRange.max) val = rateRange.max
                            // Round to 2 decimal places on blur
                            val = Math.round(val * 100) / 100
                            // Format to always show 2 decimal places
                            handleChange('rate', val.toFixed(2))

                            gtmTrackEvent('setInterestRate', { calculatorType: umbraco.Type, tab: activeTab, interestRate: val.toFixed(2) })
                          }}
                          onKeyDown={e => {
                            // Block scientific notation and signs
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                              e.preventDefault()
                            }
                            // Prevent any input when canChangeInterestRate is false
                            if (!canChangeInterestRate) {
                              e.preventDefault()
                            }
                          }}
                          className={`border-default-200 focus:border-default-400 bg-default-50 outline-none h-11 w-full rounded-lg border pb-px pl-3 pr-3 font-semibold text-base md:text-[14px] text-default-700 shadow-sm ${!canChangeInterestRate ? 'cursor-default focus:outline-none focus:ring-0 focus:border-default-200' : 'cursor-text'} disabled:pointer-events-none disabled:!text-default-400`}
                          aria-label='Interest rate number input'
                        />

                      </div>
                      {canChangeInterestRate && (
                        <div className='relative bg-default-50 h-11 w-full rounded-lg border px-3 border-default-200 items-center shadow-sm'>
                          <div className='relative flex items-center h-full'>
                            <style
                              dangerouslySetInnerHTML={{
                                __html: `
                                  .${uniqueClass} {
                                    --range-value: ${(parseFloat(shouldDisableCertainFields ? 0 : form.rate) - rateRange.min) * (100 / (rateRange.max - rateRange.min || 1))}%;
                                  }
                                `
                              }}
                            />
                            <div className='relative w-full h-2 rounded-[4px] overflow-hidden bg-default-200 before:content-[""] before:w-[var(--range-value)] before:bg-[--calc-theme-900] before:absolute before:left-0 before:top-0 before:h-full' />
                            <input
                              type='range'
                              disabled={shouldDisableCertainFields}
                              min={rateRange.min}
                              max={rateRange.max}
                              step={0.01}
                              value={shouldDisableCertainFields ? 0 : form.rate}
                              onChange={e => {
                                clearInterval(debouncedTrackInterestRate.current)
                                debouncedTrackInterestRate.current = setTimeout(() => {
                                  clearInterval(debouncedTrackInterestRate.current)

                                  gtmTrackEvent('setInterestRate', { calculatorType: umbraco.Type, tab: activeTab, interestRate: parseFloat(e.target.value).toString() })
                                }, 1000)
                                handleChange('rate', parseFloat(e.target.value))
                              }}
                              className='_loancalculator-range w-full absolute left-0 top-1/2 -translate-y-1/2 appearance-none bg-transparent cursor-pointer focus:outline-none disabled:pointer-events-none disabled:!text-default-400 disabled:opacity-80'
                            />
                            <div className='absolute h-full pointer-events-none left-[25px] w-[calc(100%_-_50px)]'>
                              <span className='absolute top-1/2 left-[var(--range-value)] size-10 z-10 -ml-5 -mt-5 text-center flex items-center justify-center text-[0.675rem] font-extrabold text-default-50'>{shouldDisableCertainFields ? 0 : form.rate}%</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Balloon Payment Amount Input (Business only) */}
                    {isBusinessCalculator && (
                      <div>
                        <label className='text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2 flex items-center gap-2'>
                          Balloon payment amount
                          {umbraco.BbpaTooltip && umbraco.BbpaTooltip.trim() && (
                            <>
                              <Tooltip id='balloon-payment-amount-tooltip' className={tooltipStyles} />
                              <span data-tooltip-id='balloon-payment-amount-tooltip' data-tooltip-content={renderTooltipTemplate(umbraco.BbpaTooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5'>
                                <InformationCircleFilled className='w-2.5 h-2.5' />
                              </span>
                            </>
                          )}
                        </label>
                        <div className='relative'>
                          <span className='absolute left-3 top-1/2 -translate-y-1/2'><Dollar className='size-3' /></span>
                          <input
                            type='number'
                            className={`${errors.balloonPaymentAmount && balloonTouched ? 'border-[--error] focus:border-[--error]' : 'border-default-200 focus:border-default-400'} bg-default-50 outline-none h-11 w-full rounded-lg border pb-px pl-8 pr-3 font-semibold text-base md:text-[14px] text-default-700 shadow-sm`}
                            placeholder='0'
                            value={form.balloonPaymentAmount}
                            onChange={e => {
                              handleChange('balloonPaymentAmount', e.target.value)

                              gtmTrackEvent('setBalloonPaymentAmount', { calculatorType: umbraco.Type, tab: activeTab, balloonPaymentAmount: e.target.value })
                              if (!balloonTouched) setBalloonTouched(true)
                            }}
                            onBlur={() => { setBalloonTouched(true); validate() }}
                            min={0}
                          />
                        </div>
                        {errors.balloonPaymentAmount && balloonTouched && <p className='text-[--error] font-extrabold text-xs mt-2'><ExclamationCircleOutline className='inline-block -translate-y-0.5' /> {errors.balloonPaymentAmount}</p>}
                      </div>
                    )}

                    {umbraco.Disclaimer.trim() && (
                      <div className='text-[12px] font-semibold text-default-500 w-full max-md:pt-4'>
                        {umbraco.Disclaimer}
                      </div>
                    )}
                  </>
                  )
                : (
                  <>
                    {/* Purpose Dropdown */}
                    <div>
                      <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I want a loan for</label>
                      <div className='relative'>
                        <select
                          className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 pb-px pr-10 pl-3 font-semibold text-base md:text-[14px] text-default-700 appearance-none shadow-sm'
                          value={afford.purpose}
                          onChange={e => {
                            handleAffordChange('purpose', e.target.value)

                            gtmTrackEvent('setPurpose', { calculatorType: umbraco.Type, tab: activeTab, purpose: e.target.value })
                          }}
                        >
                          {!hasSinglePurpose && hasValidPurposes && <option value=''>Select</option>}
                          {loanPurposes.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                      </div>
                    </div>
                    {/* Repayment Input */}
                    <div>
                      <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I can afford to repay</label>
                      <div className='relative'>
                        <span className='absolute left-3 top-1/2 -translate-y-1/2'><Dollar className='size-3' /></span>
                        <input
                          type='number'
                          className={`${affordErrors.repayment && affordTouched ? 'border-[--error] focus:border-[--error]' : 'border-default-200 focus:border-default-400'} bg-default-50 outline-none h-11 w-full rounded-lg border pb-px pl-8 pr-3 font-semibold text-base md:text-[14px] text-default-700 shadow-sm`}
                          placeholder='160'
                          value={afford.repayment}
                          onChange={e => {
                            let val = e.target.value
                            // Prevent negative numbers
                            if (val === '-') val = ''
                            if (val && parseFloat(val) < 0) return
                            handleAffordChange('repayment', val)
                            if (!affordTouched) setAffordTouched(true)
                          }}
                          onBlur={() => {
                            setAffordTouched(true)
                            validateAffordToRepay()

                            gtmTrackEvent('setRepayment', { calculatorType: umbraco.Type, tab: activeTab, repayment: afford.repayment })
                          }}
                          min={0}
                        />
                      </div>
                      {affordErrors.repayment && affordTouched && <p className='text-[--error] font-extrabold text-xs mt-2'><ExclamationCircleOutline className='inline-block' /> {affordErrors.repayment}</p>}
                    </div>
                    {/* Frequency Dropdown */}
                    <div>
                      <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I'll repay</label>
                      <div className='relative'>
                        <select
                          className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 outline-0 pb-px pr-10 pl-3 font-semibold text-base md:text-[14px] text-default-700 appearance-none shadow-sm'
                          value={afford.frequency}
                          onChange={e => {
                            handleAffordChange('frequency', e.target.value)

                            gtmTrackEvent('setFrequency', { calculatorType: umbraco.Type, tab: activeTab, frequency: e.target.value })
                          }}
                        >
                          {repaymentFrequencies.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                      </div>
                    </div>
                    {/* Term Dropdown */}
                    <div>
                      <label className='block text-[14px] font-extrabold text-default-500 max-md:mb-1 mb-2'>I'll repay over</label>
                      <div className='relative'>
                        <select
                          className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 pb-px pr-10 pl-3 font-semibold text-base md:text-[14px] text-default-700 appearance-none shadow-sm'
                          value={afford.term}
                          onChange={e => {
                            handleAffordChange('term', parseInt(e.target.value))

                            gtmTrackEvent('setTerm', { calculatorType: umbraco.Type, tab: activeTab, term: parseInt(e.target.value) })
                          }}
                        >
                          {loanTerms.map(m => (
                            <option key={m} value={m}>{formatTermLabel(m)}</option>
                          ))}
                        </select>
                        <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                      </div>
                    </div>
                    {/* Disclaimer */}
                    {umbraco.Disclaimer.trim() && (
                      <div className='text-[12px] font-bold text-default-500'>
                        {umbraco.Disclaimer}
                      </div>
                    )}
                  </>
                  )}
            </div>
            {/* Right: Overview & Actions */}
            <div className={`${showMobileCalculateButton ? 'max-md:block hidden' : 'max-md:hidden hidden'} bg-[--calc-theme-50] px-8 pb-8`}>
              <Button
                variant='secondary'
                className='w-full'
                onClick={handleCalculateButton}
                disabled={activeTab === 'loanFor' ? (!calculateRepaymentResults || !!errors.amount || !form.amount) : (!calculateMaxBorrowableResults || !!affordErrors.repayment || !afford.repayment)}
              >
                Calculate
              </Button>
            </div>
            <div ref={mobileCalculatorBlock} className={`bg-[--calc-theme-50] flex-1 flex flex-col items-center justify-center md:min-h-[540px] min-w-[320px] px-8 lg:px-12 pt-4 pb-8 md:py-8 ${showMobileCalculateButton ? 'max-md:hidden' : 'max-md:block'}`}>
              <div className={`${showBreakdown ? 'opacity-0' : 'opacity-100'} transition-opacity duration-600 w-full h-full rounded-2xl bg-[--calc-theme-950] bg-gradient-to-r from-0% from-[--calc-theme-900] to-[--calc-theme-950] flex flex-col items-center justify-center py-8 lg:py-12 px-6 lg:px-12 gap-10 max-md:pt-2 max-md:pb-6`}>
                <div className='w-full max-w-full flex flex-col gap-3 max-md:gap-0'>
                  {activeTab === 'loanFor'
                    ? (
                      <>
                        <div className='flex flex-col items-center w-full max-md:flex-row'>
                          <span className='text-[16px] font-extrabold max-md:font-medium text-default-50 max-md:w-20 max-md:shrink-0 max-md:text-sm'>This will cost about</span>
                          <span className={`${calculateRepaymentResults && calculateRepaymentResults.periodicRepaymentWithMaintenance && formatCurrency(calculateRepaymentResults.periodicRepaymentWithMaintenance).length > 9 ? 'text-4xl max-md:text-3xl' : 'text-[52px] max-md:text-4xl'}  font-extrabold max-md:justify-end text-default-50 tracking-tight flex items-center justify-center h-[78px] max-md:grow`}>{(calculateRepaymentResults && !isNaN(calculateRepaymentResults.periodicRepaymentWithMaintenance) && isFinite(calculateRepaymentResults.periodicRepaymentWithMaintenance)) ? formatCurrency(calculateRepaymentResults.periodicRepaymentWithMaintenance) : '$0.00'}</span>
                        </div>
                      </>
                      )
                    : (
                      <>
                        <div className='flex flex-col items-center w-full'>
                          <span className='text-[16px] font-extrabold text-default-50'>{afford.purpose !== '' ? `You can buy a ${afford.purpose} worth` : 'You could borrow'}</span>
                          <span className={`${calculateMaxBorrowableResults && calculateMaxBorrowableResults.results?.display && calculateMaxBorrowableResults.results?.display?.loanAmount && formatCurrency(calculateMaxBorrowableResults.results?.display?.loanAmount).length > 9 ? 'text-4xl' : 'text-[52px]'} font-extrabold text-default-50 tracking-tight flex items-center justify-center h-[78px]`}>{calculateMaxBorrowableResults && calculateMaxBorrowableResults.results?.display ? formatCurrency(calculateMaxBorrowableResults.results?.display?.loanAmount) : '$0.00'}</span>
                        </div>
                      </>
                      )}
                  {/* Repayment Frequency Buttons (only for loanFor tab) */}
                  {activeTab === 'loanFor' && (
                    <div className='w-full mb-5 max-md:mb-3'>
                      <div className='flex md:hidden w-full relative'>
                        <select
                          className='cursor-pointer h-11 text-default-900 font-semibold text-base md:text-[14px] appearance-none pb-px pl-3 pr-6 outline-none bg-default-50 rounded-lg shadow w-full'
                          value={form.frequency}
                          onChange={e => {
                            handleChange('frequency', e.target.value)

                            gtmTrackEvent('setFrequency', { calculatorType: umbraco.Type, tab: activeTab, frequency: e.target.value })
                          }}
                        >
                          {repaymentFrequencies.map(freq => (
                            <option key={freq.value} value={freq.value}>{freq.label}</option>
                          ))}
                        </select>
                        <Chevron className='text-[--calc-theme-chevron] pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                      </div>
                      <div className='hidden md:flex w-full gap-2'>
                        {repaymentFrequencies.map(freq => (
                          <button
                            key={freq.value}
                            className={`flex-1 h-8 pb-1 rounded font-extrabold text-xs md-custom:text-sm border-2 ${form.frequency === freq.value ? 'bg-default-50 text-default-900 border-default-50' : 'bg-transparent text-default-50 border-default-50'}`}
                            onClick={() => {
                              handleChange('frequency', freq.value)

                              gtmTrackEvent('setFrequency', { calculatorType: umbraco.Type, tab: activeTab, frequency: freq.value })
                            }}
                          >
                            {freq.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className='flex flex-col w-full gap-3'>
                    <Button
                      className={`_calc-button-primary ${themeCta !== '' ? '!bg-[--calc-cta] hover:!bg-transparent transition duration-300' : ''} ${themeSecondary !== '' ? 'before:!bg-white hover:before:!opacity-20' : ''}`}
                      variant='primary'
                      disabled={activeTab === 'loanFor' ? (!calculateRepaymentResults || !!errors.amount || !form.amount) : (!calculateMaxBorrowableResults || !!affordErrors.repayment || !afford.repayment)}
                      onClick={() => {
                        setShowBreakdown(true)

                        gtmTrackEvent('showBreakdown', { calculatorType: umbraco.Type, tab: activeTab })
                      }}
                    >
                      See Breakdown
                    </Button>
                    {umbraco.ApplyNowButton?.Url && !umbraco.HideApplyNowButton && (
                      <Button
                        className={`_calc-button-secondary ${themeApplyNowColor !== '' ? '!bg-[--calc-apply-now] hover:!bg-[#8e8e8e] transition duration-300' : themeCta !== '' ? '!bg-black hover:!bg-[#818181] transition duration-300 _apply-now-button-dark-shadow' : ''} ${themeSecondary !== '' ? 'before:!bg-white hover:before:!opacity-20' : (themeApplyNowColor !== '' || themeCta !== '') ? 'before:!opacity-0 hover:before:!opacity-0' : ''}`}
                        variant='secondary'
                        disabled={activeTab === 'loanFor' ? (!calculateRepaymentResults || !!errors.amount || !form.amount) : (!calculateMaxBorrowableResults || !!affordErrors.repayment || !afford.repayment)}
                        isApplyDialogTriggerButton={umbraco.ApplyNowButton.Url === '#apply-dialog'}
                        href={umbraco.ApplyNowButton.Url}
                        componentName='Loan Calculator'
                      >
                        Apply Now
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Breakdown fee row
function BreakdownFee ({ label, value, tooltip, uid, tooltipConstants }) {
  return (
    <dl className='flex flex-col gap-2 w-full max-md:gap-0.5 max-md:px-4'>
      <dt className='text-sm text-pretty font-extrabold text-default-300 w-full flex items-center max-md:gap-1.5 gap-1 relative'>
        {label}
        {tooltip && tooltip.trim() && (
          <>
            <Tooltip id={uid} className={tooltipStyles} />
            <span data-tooltip-id={uid} data-tooltip-content={renderTooltipTemplate(tooltip, tooltipConstants)} className='size-5 rounded-full flex items-center justify-center relative -left-1.5 -top-0.5'>
              <InformationCircleFilled className='w-2.5 h-2.5' />
            </span>
          </>
        )}
      </dt>
      <dd className='h-11 max-md:h-auto rounded-lg flex items-center text-sm px-3 max-md:px-0 font-semibold text-default-50 bg-white/10 max-md:bg-transparent'>{value}</dd>
    </dl>
  )
}

// Breakdown overview row
function BreakdownOverview ({ label, value }) {
  return (
    <dl className='flex flex-col max-md:flex-row gap-2 w-full max-md:items-center'>
      <dt className='text-sm max-md:font-medium [@media(max-width:380px)]:text-xs font-extrabold max-md:w-1/2 text-default-300'>{label}</dt>
      <dd className='max-md:shrink-0 flex max-md:w-1/2 w-full h-11 max-md:h-10 items-center px-3 rounded-lg bg-white/10 text-sm font-semibold text-default-300'>{value}</dd>
    </dl>
  )
}

reactMount('loanCalculatorBlock', loanCalculatorBlock)
